import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Shield, AlertTriangle, ChevronRight, Plus, X, FileWarning, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';
import { formatDate } from '../lib/constants';
import QuotaBanner from '../components/QuotaBanner';
import UpgradeRequestModal from '../components/UpgradeRequestModal';
import { useQuotaUsage } from '../lib/useQuotaUsage';
import MissionShell from '../components/MissionShell';
import { MISSIONS } from '../lib/missions';

const STATUS_TONE = {
  borrador:                 { label: 'Borrador',                color: '#6B7280', bg: '#F3F4F6' },
  en_revision:              { label: 'En revisión',             color: '#1E40AF', bg: '#EFF6FF' },
  aprobada:                 { label: 'Aprobada',                color: '#065F46', bg: '#ECFDF5' },
  aprobada_con_condiciones: { label: 'Aprobada c/ condiciones', color: '#065F46', bg: '#ECFDF5' },
  requiere_consulta:        { label: 'Consulta a Agencia',      color: '#7F1D1D', bg: '#FEE2E2' },
  vencida:                  { label: 'Vencida',                 color: '#B91C1C', bg: '#FEF2F2' },
};

export default function Dpia() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [ratNeeds, setRatNeeds] = useState([]);
  const [ratCount, setRatCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null); // { activity_id, activity_name } | 'blank' | null
  const [title, setTitle] = useState('');
  const [showUpgrade, setShowUpgrade] = useState(false);
  const { usage, refresh: refreshUsage } = useQuotaUsage();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, rat, acts] = await Promise.all([
        api.get('/dpia'), api.get('/dpia/rat-with-triggers'), api.get('/rat/activities'),
      ]);
      setItems(list.data);
      setRatNeeds(rat.data);
      setRatCount(acts.data.length);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      const payload = { title: title.trim() || `DPIA — ${creating.activity_name || 'Nueva evaluación'}` };
      if (creating !== 'blank') payload.activity_id = creating.activity_id;
      const { data } = await api.post('/dpia', payload);
      toast.success('DPIA creada');
      refreshUsage();
      nav(`/app/dpia/${data.id}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === 'object' && d.error === 'quota_exceeded') {
        toast.error(d.message || 'Alcanzó el tope de DPIAs.');
        setCreating(null);
        setShowUpgrade(true);
      } else {
        toast.error(d || 'Error');
      }
    }
  };

  const pending = ratNeeds.filter((r) => !r.dpia_id);

  if (loading) return <div className="text-sm text-[#6B7280]">Cargando…</div>;

  const approved = items.filter((d) => ['aprobada', 'aprobada_con_condiciones'].includes(d.status)).length;

  return (
    <MissionShell
      mission={MISSIONS.dpia}
      idPrefix="dpia-mission"
      prereq={ratCount === 0 ? { message: 'Para detectar qué actividades son de alto riesgo, primero completa tu inventario de datos.', to: '/app/rat', cta: 'Ir a Inventario de datos' } : null}
      doHint={pending.length > 0
        ? `Siguiente acción: tienes ${pending.length} actividad${pending.length === 1 ? '' : 'es'} de alto riesgo sin evaluación. Ábrelas desde la lista sugerida.`
        : 'No hay actividades de alto riesgo pendientes de evaluar. Revisa tus evaluaciones cuando cambie un tratamiento.'}
      evidence={[
        { label: 'Evaluaciones creadas', value: items.length, tone: 'ok' },
        { label: 'Aprobadas', value: `${approved} de ${items.length}`, tone: items.length && approved < items.length ? 'warn' : 'ok' },
        { label: 'Alto riesgo sin evaluar', value: pending.length, tone: pending.length ? 'bad' : 'ok' },
      ]}
      evidenceNote="Cada evaluación guarda su matriz de riesgo, medidas y decisión del DPO como respaldo ante la Agencia."
    >
    <div className="space-y-6" data-testid="dpia-page">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#E5E7EB] pb-5">
        <div>
          <div className="text-[15px] font-medium text-[#1C2329]">
            {items.length} DPIA{items.length === 1 ? '' : 's'} · {pending.length} RAT sin DPIA con alto riesgo
          </div>
          <div className="text-[13px] text-[#6B7280]">
            Priviax detecta actividades con alto riesgo y sugiere abrir una Evaluación de Impacto (art. 15 bis).
          </div>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-[#143A34] hover:bg-[#0F2A26] text-white font-medium text-[13px]"
          onClick={() => { setCreating('blank'); setTitle(''); }} data-testid="dpia-new">
          <Plus size={14} /> Nueva DPIA en blanco
        </button>
      </div>

      <QuotaBanner usage={usage} quotaKey="dpia_year" onRequestUpgrade={() => setShowUpgrade(true)} />
      <UpgradeRequestModal open={showUpgrade} onClose={(sent) => { setShowUpgrade(false); if (sent) refreshUsage(); }} />

      {/* RATs sugeridos */}
      {pending.length > 0 && (
        <section>
          <div className="text-[11.5px] uppercase tracking-wider text-[#B45309] font-semibold mb-2 flex items-center gap-1.5">
            <FileWarning size={13} /> RAT con alto riesgo · requieren DPIA
          </div>
          <div className="border border-[#FDE68A] bg-[#FFFBEB]">
            {pending.map((r) => (
              <button key={r.activity_id} onClick={() => { setCreating(r); setTitle(`DPIA — ${r.activity_name}`); }}
                className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-[#FEF3C7] border-b border-[#FDE68A] last:border-b-0"
                data-testid={`dpia-rat-${r.activity_id}`}>
                <AlertTriangle size={14} className="text-[#B45309] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-[#1C2329] truncate">{r.activity_name}</div>
                  <div className="text-[11px] text-[#92400E] truncate">
                    Triggers: {r.triggers.join(' · ')}
                  </div>
                </div>
                <span className="text-[11px] text-[#92400E] font-medium">Crear DPIA →</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* DPIAs existentes */}
      <section>
        <div className="text-[11.5px] uppercase tracking-wider text-[#6B7280] font-semibold mb-2">Evaluaciones existentes</div>
        {items.length === 0 ? (
          <div className="border border-[#E5E7EB] bg-white p-10 text-center">
            <Shield size={26} className="mx-auto text-[#9CA3AF]" strokeWidth={1.5} />
            <div className="mt-3 text-lg font-semibold text-[#1C2329]">Sin DPIAs registradas</div>
            <div className="text-sm text-[#6B7280] mt-1 max-w-md mx-auto">
              Cree una DPIA desde un RAT sugerido arriba o en blanco.
            </div>
          </div>
        ) : (
          <div className="border border-[#E5E7EB] bg-white divide-y divide-[#E5E7EB]">
            {items.map((d) => {
              const tone = STATUS_TONE[d.status] || STATUS_TONE.borrador;
              return (
                <button key={d.id} onClick={() => nav(`/app/dpia/${d.id}`)}
                  className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-[#FAFBFC]"
                  data-testid={`dpia-row-${d.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-[#1C2329] truncate">{d.title}</div>
                    <div className="text-[11px] text-[#6B7280] truncate">
                      {d.activity_name ? `RAT: ${d.activity_name}` : 'Sin RAT vinculado'}
                      {d.approved_at && ` · Aprobada ${formatDate(d.approved_at)}`}
                      {d.next_review_at && ` · Próx. revisión ${formatDate(d.next_review_at)}`}
                    </div>
                  </div>
                  <span className="text-[10.5px] font-medium px-1.5 py-0.5" style={{ color: tone.color, backgroundColor: tone.bg }}>
                    {tone.label}
                  </span>
                  <ChevronRight size={13} className="text-[#9CA3AF]" />
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Modal crear */}
      {creating && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setCreating(null)}>
          <div className="bg-white max-w-md w-full p-5 border-t-4 border-[#143A34]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[15px] font-semibold text-[#1C2329]">Nueva DPIA</div>
                {creating !== 'blank' && (
                  <div className="text-[11.5px] text-[#6B7280] mt-0.5">RAT vinculado: {creating.activity_name}</div>
                )}
              </div>
              <button onClick={() => setCreating(null)}><X size={16} className="text-[#6B7280]" /></button>
            </div>
            <label className="text-[12px] text-[#374151] font-medium">Título</label>
            <input className="pv-input mt-1" placeholder="Ej: DPIA - Sistema de scoring crediticio"
              value={title} onChange={(e) => setTitle(e.target.value)} data-testid="dpia-title" />
            <div className="flex justify-end gap-2 mt-4">
              <button className="pv-btn-secondary" onClick={() => setCreating(null)}>Cancelar</button>
              <button className="px-3 py-1.5 bg-[#143A34] text-white text-[12.5px] font-medium disabled:opacity-50"
                disabled={title.trim().length < 3} onClick={create} data-testid="dpia-save">
                <CheckCircle2 size={12} className="inline mr-1" /> Crear
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </MissionShell>
  );
}
